import React from 'react';
import CommandDebugger from '@/components/CommandDebugger';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Info, Code } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Voice Command Debugger Page
 * 
 * A developer-focused page for testing, analyzing, and understanding
 * the voice command system. Provides tools for documentation, testing,
 * and analytics.
 */
const VoiceCommandDebuggerPage = () => {
  return (
    <div className="container max-w-6xl py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2">
            <Link to="/demo">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Demo
            </Link>
          </Button>
          <h1 className="text-3xl font-bold">Voice Command Debugger</h1>
          <p className="text-muted-foreground mt-1">
            Developer tools for testing, documenting, and analyzing voice commands
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href="https://github.com/username/repo/tree/main/src/utils/voiceCommandUtils.ts" target="_blank" rel="noopener noreferrer">
              <Code className="mr-2 h-4 w-4" />
              View Source
            </a>
          </Button>
          
          <Button variant="outline" size="sm" asChild>
            <a href="#documentation">
              <BookOpen className="mr-2 h-4 w-4" />
              Documentation
            </a>
          </Button>
        </div>
      </div>
      
      <div className="mb-8 bg-muted p-4 rounded-lg">
        <div className="flex gap-3 items-start">
          <Info className="h-5 w-5 text-blue-500 mt-0.5" />
          <div>
            <h3 className="font-medium">Developer Mode</h3>
            <p className="text-sm text-muted-foreground">
              This page helps developers test and debug voice commands. You can:
            </p>
            <ul className="text-sm list-disc pl-5 mt-2 space-y-1">
              <li>Test voice commands and see how they're processed</li>
              <li>View complete documentation of supported command patterns</li>
              <li>Analyze command usage patterns and success rates</li>
              <li>Identify problematic commands that fail frequently</li>
            </ul>
          </div>
        </div>
      </div>
      
      <CommandDebugger />
      
      <div id="documentation" className="mt-12 pt-6 border-t">
        <h2 className="text-2xl font-bold mb-4">Voice Command Documentation</h2>
        
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-semibold mb-2">Command Types</h3>
            <p className="mb-3">
              The voice command system supports several command categories:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-md p-4">
                <h4 className="font-medium mb-1">Navigation</h4>
                <p className="text-sm text-muted-foreground">
                  Commands that allow users to navigate between pages.
                  Examples: "go to home", "open settings", "go back"
                </p>
              </div>
              
              <div className="border rounded-md p-4">
                <h4 className="font-medium mb-1">Search</h4>
                <p className="text-sm text-muted-foreground">
                  Commands that initiate search operations.
                  Examples: "search for help", "find settings", "where is profile"
                </p>
              </div>
              
              <div className="border rounded-md p-4">
                <h4 className="font-medium mb-1">Accessibility</h4>
                <p className="text-sm text-muted-foreground">
                  Commands that control accessibility features.
                  Examples: "dark mode on", "increase font size", "enable large cursor"
                </p>
              </div>
              
              <div className="border rounded-md p-4">
                <h4 className="font-medium mb-1">Help</h4>
                <p className="text-sm text-muted-foreground">
                  Commands that provide assistance information.
                  Examples: "help", "what can you do", "show commands"
                </p>
              </div>
              
              <div className="border rounded-md p-4">
                <h4 className="font-medium mb-1">Custom</h4>
                <p className="text-sm text-muted-foreground">
                  Application-specific commands defined by developers.
                  These can be added through the plugin system.
                </p>
              </div>
            </div>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold mb-2">Custom Command Plugins</h3>
            <p className="mb-3">
              Developers can extend the command system with custom plugins:
            </p>
            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-md overflow-x-auto">
              <pre className="text-xs">
{`// Example of registering custom command plugins
const config = {
  // ... other configuration
  customCommandPlugins: [
    {
      triggers: "open menu",
      action: () => toggleMenu(true),
      alias: ["show menu", "display menu"],
      description: "Opens the main application menu"
    },
    {
      triggers: ["log out", "sign out"],
      action: () => logoutUser(),
      condition: (cmd, context) => isUserLoggedIn(),
      description: "Signs the current user out"
    }
  ]
};`}
              </pre>
            </div>
          </section>
          
          <section>
            <h3 className="text-xl font-semibold mb-2">Compound Commands</h3>
            <p className="mb-3">
              The system supports compound commands that combine multiple actions:
            </p>
            <div className="bg-muted p-4 rounded-md">
              <ul className="space-y-1 text-sm">
                <li><strong>"go to home and search for settings"</strong> - Navigates to home, then searches for "settings"</li>
                <li><strong>"turn on dark mode and increase font size"</strong> - Enables dark mode, then increases font size</li>
                <li><strong>"go back then show help"</strong> - Navigates back, then shows help information</li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default VoiceCommandDebuggerPage;
